<template>
  <div>
    <v-card
      elevation="5"
    >
      <v-toolbar
        color="primaryLight"
        dark
        dense
      >
        <v-toolbar-title>Tiempo de actuación</v-toolbar-title>
      </v-toolbar>

      <v-card-text
        v-if="loading || error || combinedData.length"
      >
        <div
          class="chart-container"
          :style="{ height: chartHeight + 'px' }"
        >
          <div
            v-if="loading"
            class="chart-state"
          >
            <v-progress-circular
              indeterminate
              color="primaryLight"
            />
          </div>
          <div
            v-else-if="error"
            class="chart-state"
          >
            <span
              class="grey--text"
            >No se pudieron cargar los datos.</span>
          </div>
          <canvas
            v-else
            ref="ganttChart"
          />
        </div>
      </v-card-text>
      <p
        v-if="!loading && !error && !combinedData.length"
        class="text-center pa-4 grey--text"
      >
        Sin medios asignados
      </p>
    </v-card>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import DateService, { FMT } from '@/helpers/DateService'
import { mapGetters } from 'vuex'
import api from '@/api'

const MAX_LLEGADA_HORAS = 8

const segmentLabelsPlugin = {
  id: 'segmentLabels',
  afterDatasetsDraw(chart) {
    const { ctx: c } = chart
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (meta.hidden) return
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index]
        if (!value || value <= 0) return
        const { x, base, y } = bar.getProps(['x', 'base', 'y'], true)
        const segmentWidth = Math.abs(x - base)
        if (segmentWidth < 28) return
        c.save()
        c.fillStyle = '#fff'
        c.font = 'bold 11px sans-serif'
        c.textAlign = 'center'
        c.textBaseline = 'middle'
        c.fillText(`${value}h`, (x + base) / 2, y)
        c.restore()
      })
    })
  }
}

export default {
  props: {
    idIncendio: { type: String, default: null, required: true }
  },

  data: () => ({
    chart: null,
    horasJornada: [],
    tiemposLlegada: [],
    loading: false,
    error: null,
    requestToken: 0
  }),

  computed: {
    ...mapGetters('incendio', ['getIncendioByID']),
    ...mapGetters('parametro', ['getParametro']),

    incendio() {
      return this.getIncendioByID(this.idIncendio)
    },

    medios() {
      return this.incendio?.SECTORES?.flatMap(s => s.MEDIOS || []) || []
    },

    limiteJornadaHoras() {
      const raw = this.getParametro('DEFAULT_TIEMPO_ACTUACION')
      return Number.isFinite(raw) && raw > 0 ? raw : 12
    },

    fechasDelDia() {
      return [...new Set(
        this.medios
          .map(m => {
            const d = DateService.toDayjs(m.FECHA_ALARMADO)
            return d?.isValid() ? d.format(FMT.onlyDateDash) : null
          })
          .filter(Boolean)
      )]
    },

    combinedData() {
      const multi = this.fechasDelDia.length > 1
      const map = new Map()

      this.horasJornada.forEach(item => {
        const key = `${item.idMedio}_${item.fecha}`
        if (!map.has(key)) {
          map.set(key, emptyEntry(multi ? `${item.nombreMedio} (${item.fecha})` : item.nombreMedio))
        }
        const entry = map.get(key)
        const horas = item.desglose.reduce((sum, d) => sum + d.minutos, 0) / 60
        entry.jornadaTrabajadas = +horas.toFixed(2)
        entry.jornadaRestantes = +Math.max(this.limiteJornadaHoras - horas, 0).toFixed(2)
        entry.jornadaDesglose = item.desglose
      })

      this.tiemposLlegada.forEach(item => {
        const key = `${item.idMedio}_${item.fecha}`
        if (!map.has(key)) {
          map.set(key, emptyEntry(multi ? `${item.nombreMedio} (${item.fecha})` : item.nombreMedio))
        }
        const entry = map.get(key)
        const horas = item.desglose.reduce((sum, d) => sum + d.minutos, 0) / 60
        entry.llegadaViaje = +horas.toFixed(2)
        entry.llegadaRestantes = +Math.max(MAX_LLEGADA_HORAS - horas, 0).toFixed(2)
        entry.llegadaDesglose = item.desglose
      })

      return [...map.values()]
    },

    chartHeight() {
      if (this.loading || this.error) return 100
      return Math.max(200, this.combinedData.length * 70 + 80)
    }
  },

  watch: {
    idIncendio(newVal, oldVal) {
      if (newVal && newVal !== oldVal) this.fetchAll()
    },

    fechasDelDia(newVal, oldVal) {
      if (newVal.length && newVal.join(',') !== oldVal.join(',')) this.fetchAll()
    }
  },

  mounted() {
    if (this.idIncendio && this.fechasDelDia.length) this.fetchAll()
  },

  beforeDestroy() {
    if (this.chart) this.chart.destroy()
  },

  methods: {
    async fetchAll() {
      if (!this.idIncendio || !this.fechasDelDia.length) return
      this.requestToken++
      const token = this.requestToken
      this.loading = true
      this.error = null
      try {
        const [jornadaResults, llegadaResults] = await Promise.allSettled([
          Promise.all(this.fechasDelDia.map(fecha =>
            api.medio.getHorasJornadaDia({ fecha, idIncendio: this.idIncendio })
          )),
          Promise.all(this.fechasDelDia.map(fecha =>
            api.medio.getTiemposLlegadaDia({ fecha, idIncendio: this.idIncendio })
          ))
        ])
        if (token === this.requestToken && !this._isDestroyed) {
          if (jornadaResults.status === 'rejected' && llegadaResults.status === 'rejected') {
            this.error = jornadaResults.reason
            this.$notify({ type: 'error', text: 'No se pudieron cargar los diagramas GANTT.' })
          } else {
            if (jornadaResults.status === 'fulfilled') {
              this.horasJornada = this.fechasDelDia.flatMap((fecha, i) =>
                jornadaResults.value[i].data.map(item => ({ ...item, fecha }))
              )
            }
            if (llegadaResults.status === 'fulfilled') {
              this.tiemposLlegada = this.fechasDelDia.flatMap((fecha, i) =>
                llegadaResults.value[i].data.map(item => ({ ...item, fecha }))
              )
            }
            this.$nextTick(() => { this.$nextTick(() => { this.renderChart() }) })
          }
        }
      } catch (err) {
        if (token === this.requestToken && !this._isDestroyed) {
          this.error = err
          this.$notify({ type: 'error', text: 'No se pudieron cargar los diagramas GANTT.' })
        }
      } finally {
        if (token === this.requestToken && !this._isDestroyed) {
          this.loading = false
        }
      }
    },

    renderChart() {
      const canvas = this.$refs.ganttChart
      if (!canvas) return
      if (this.chart) { this.chart.destroy(); this.chart = null }

      const xMax = Math.max(this.limiteJornadaHoras, MAX_LLEGADA_HORAS)
      const data = this.combinedData

      this.chart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        plugins: [segmentLabelsPlugin],
        data: {
          labels: data.map(d => d.label),
          datasets: [
            {
              label: 'Jornada - Utilizadas',
              stack: 'jornada',
              data: data.map(d => d.jornadaTrabajadas),
              backgroundColor: '#f44336'
            },
            {
              label: `Jornada - Disponibles (hasta ${this.limiteJornadaHoras}h)`,
              stack: 'jornada',
              data: data.map(d => d.jornadaRestantes),
              backgroundColor: '#4caf50'
            },
            {
              label: 'Llegada - Tiempo de viaje',
              stack: 'llegada',
              data: data.map(d => d.llegadaViaje),
              backgroundColor: '#d32f2f'
            },
            {
              label: `Llegada - Disponible (hasta ${MAX_LLEGADA_HORAS}h)`,
              stack: 'llegada',
              data: data.map(d => d.llegadaRestantes),
              backgroundColor: '#2e7d32'
            }
          ]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                afterBody: (items) => {
                  const item = items[0]
                  if (!item) return []
                  const entry = data[item.dataIndex]
                  const label = item.dataset.label
                  let desglose = []
                  if (label === 'Jornada - Utilizadas') desglose = entry.jornadaDesglose
                  else if (label === 'Llegada - Tiempo de viaje') desglose = entry.llegadaDesglose
                  if (!desglose.length) return []
                  return desglose.map(d => `  ${d.nombreIncendio}: ${(d.minutos / 60).toFixed(2)}h`)
                }
              }
            }
          },
          scales: {
            x: { stacked: true, max: xMax },
            y: { stacked: true }
          }
        }
      })
    }
  }
}

function emptyEntry(label) {
  return {
    label,
    jornadaTrabajadas: 0,
    jornadaRestantes: 0,
    jornadaDesglose: [],
    llegadaViaje: 0,
    llegadaRestantes: 0,
    llegadaDesglose: []
  }
}
</script>

<style scoped>
.chart-container { position: relative; width: 100%; }
.chart-state { display: flex; align-items: center; justify-content: center; height: 100%; }
</style>
